import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { FieldChip } from 'components/field-value/field-value.component';

// The row's 16px top margin plus one small (24px) chip. The drawer shrinks its tab content by this
// much when the row shows (index.component.js), and the row never wraps, so it's always one row.
export const PINNED_CHIP_ROW_HEIGHT_PX = 40;

/**
 * The chip row under the customer's name (CIN-7177): every Customer field an admin pinned on the
 * Data Fields page, in the model's order, so key facts ("Spend · Top 20%", "VIP") show on every
 * drawer tab. At most four; the rest fold into "+N" with a tooltip.
 *
 * `chips` is pinnedChips()'s { shown, overflow }. The drawer computes it once, because it also
 * needs it to size the tab content. Renders nothing when no field is pinned, so drawers for
 * companies that never set this up look exactly as before.
 */
export function PinnedFieldChips({
  chips
}) {
  const {
    shown = [],
    overflow = []
  } = chips || {};
  if (!shown.length) return null;
  return (
    // Long chips truncate instead of wrapping; "+N" never shrinks.
    <Box sx={{
      'display': 'flex',
      'flexWrap': 'nowrap',
      'overflow': 'hidden',
      'gap': '8px',
      'mt': '16px',
      '& > .MuiChip-root': {
        flexShrink: 1,
        minWidth: 0
      },
      '& > [data-testid="pinned-field-overflow"]': {
        flexShrink: 0
      }
    }} data-testid="pinned-field-chips">
      {shown.map(chip =>
      // The full "Field: value", since a long chip is truncated. Focusable, so keyboard users get
      // the tooltip too. describeChild: the chip's own text stays its name, and the tooltip
      // describes it.
      <Tooltip key={chip.key} title={`${chip.fieldName}: ${chip.text}`} describeChild arrow>
          <FieldChip text={chip.text} color={chip.color} tabIndex={0} data-testid="pinned-field-chip" />
        </Tooltip>)}
      {overflow.length > 0 && <Tooltip arrow describeChild title={<Box component="ul" sx={{
        m: 0,
        pl: '16px'
      }}>
              {overflow.map(chip => <li key={chip.key}>
                  {chip.fieldName}: {chip.text}
                </li>)}
            </Box>}>
          {/* Focusable, so keyboard users can open the list of the chips that didn't fit. */}
          <FieldChip text={`+${overflow.length}`} color="slate" tabIndex={0} aria-label={`${overflow.length} more pinned ${overflow.length === 1 ? 'value' : 'values'}`} data-testid="pinned-field-overflow" />
        </Tooltip>}
    </Box>
  );
}